"use client";

import React, { FC, useState, useEffect, ComponentType } from "react";
import { createPortal } from "react-dom";
import { BottomNavigationMobile } from "../../interfaces/IHeader.interface";
import { getChildByNodeName } from "@repo/shared-utils/common-utils";
import Conditional from "@repo/shared-ui/Conditional";
import dynamic from "next/dynamic";
import { SignOutProps } from "./SignOut";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/web-store";
import { LOGGED_IN_MY_ACC_URL, NON_LOGGED_IN_MY_ACC_URL } from "../../constants/HeaderConstants";
import BFLLink from "@repo/shared-ui/BFLLink";
import FollowUsList from "./FollowUsList";
import FirstName from "./FirstName";
import {
  handleHeaderMenuClickDL,
  handleHeaderMenuCloseClickDL,
  handleHeaderMainMenuClickDL,
  handleHeaderSidebarProfileClickDL,
  handleHeaderSidebarMenuButtonClickDL,
} from "./HeaderAnalyticsFunction";

interface NavigatorWithUAData extends Navigator {
  userAgentData?: {
    platform?: string;
  };
}

const SignOutLink: ComponentType<SignOutProps> = dynamic(() => import("./SignOut.tsx") as any, {
  ssr: false,
});
interface MenuToggleProps {
  bottomnavigationmobile?: BottomNavigationMobile | BottomNavigationMobile[];
}

const MenuToggle: FC<MenuToggleProps> = ({ bottomnavigationmobile }) => {
  interface HamburgerMenuChild {
    nodeName: string;
    [key: string]: any;
  }

  interface BottomNavigationMobileWithChildren extends BottomNavigationMobile {
    children?: HamburgerMenuChild[];
  }

  const bottomNavigationMobileNode = Array.isArray(bottomnavigationmobile)
    ? bottomnavigationmobile[0]
    : bottomnavigationmobile;

  const hamburgerMenu = (
    bottomNavigationMobileNode as BottomNavigationMobileWithChildren | undefined
  )?.children?.find?.((item: HamburgerMenuChild) => item.nodeName === "hamburgermenu");

  // Helper function to safely extract menu items
  const extractMenuItems = (node: any): any[] => {
    if (!node) return [];

    // If it's an array, return it directly
    if (Array.isArray(node)) return node;

    // If it has a children property that's an array, return that
    if (node.children && Array.isArray(node.children)) return node.children;

    // Otherwise return an empty array
    return [];
  };

  // Get the menu lists and ensure they have a default value if missing
  const hamburgerMenuListNode = getChildByNodeName(hamburgerMenu, "hamburgerMenuList");
  const mainMenuListNode = getChildByNodeName(hamburgerMenu, "mainMenuList");
  const followUsListnode = getChildByNodeName(hamburgerMenu, "followUsMenuList");

  // Get children arrays from the menu nodes using the helper function
  const hamburgerMenuList = extractMenuItems(hamburgerMenuListNode);
  const mainMenuList = extractMenuItems(mainMenuListNode);
  const followUsList = extractMenuItems(followUsListnode);

  const getMenuTitle = (item: any): string =>
    item?.secondaryMenuTitle ||
    item?.secondaryNavLabel ||
    item?.mainMenuListTitle ||
    item?.hamburgerMenuTitle ||
    item?.chipsTitle ||
    "";

  const getMenuHref = (item: any): string =>
    item?.hamburgerCtaLink ||
    item?.hamburgerMenuCtaLink ||
    item?.mainMenuCtaLink ||
    item?.primaryCtaLink ||
    item?.secondaryCtaLink ||
    item?.ctaLink ||
    item?.chipsCtaLink ||
    "";

  const shouldOpenInNewTab = (item: any): boolean =>
    item?.ctaOpenInNewTab === "true" ||
    item?.mainMenuOpenInNewTab === "true" ||
    item?.openInNewTab === "true";

  const hasRenderableMenuFields = (item: any): boolean =>
    Boolean(getMenuTitle(item) || getMenuHref(item));

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedSubMenu, setSelectedSubMenu] = useState<any[]>([]);
  const [selectedMenuCtaLink, setSelectedMenuCtaLink] = useState("");
  const [mounted, setMounted] = useState(false);
  const [isWindows, setIsWindows] = useState(false);
  const isAuthSuccessOrPerpetualRaw = useAppSelector(selectIsAuthSuccessOrPerpetual);
  // Defer auth state to client-only to prevent SSR/client hydration mismatch
  const isAuthSuccessOrPerpetual = mounted && isAuthSuccessOrPerpetualRaw;

  useEffect(() => {
    setMounted(true);
    const navigatorWithUAData = navigator as NavigatorWithUAData;
    const platform = navigatorWithUAData.userAgentData?.platform ?? navigator.userAgent ?? "";
    setIsWindows(/win/i.test(platform));
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    // Track menu button click analytics
    handleHeaderMenuClickDL("Menu");

    setIsSidebarOpen((prev) => !prev);
    setShowSubMenu(false);
  };

  const closeSidebar = () => {
    // Track menu close button click analytics
    handleHeaderMenuCloseClickDL("Close Menu");

    setIsSidebarOpen(false);
    setShowSubMenu(false);
  };

  // Helper function to find submenu items
  const findSubmenuItems = (item: any, detailsNodeName: string): any[] => {
    // Check for direct property access first
    const directPropertyName =
      detailsNodeName === "secondaryMenuDetails" ? "secondaryMenuDetails" : "mainMenuDetails";

    if (Array.isArray(item?.[directPropertyName])) {
      return item[directPropertyName];
    }

    // Look in children array if it exists
    if (item?.children && Array.isArray(item.children)) {
      const detailsNode = item.children.find((child: any) => child?.nodeName === detailsNodeName);

      if (detailsNode?.children && Array.isArray(detailsNode.children)) {
        return detailsNode.children;
      }

      const sectionItemsNode = item.children.find((child: any) => child?.nodeName === "sectionItems");
      if (sectionItemsNode?.children && Array.isArray(sectionItemsNode.children)) {
        return sectionItemsNode.children;
      }

      const renderableChildren = item.children.filter(hasRenderableMenuFields);
      if (renderableChildren.length > 0) {
        return renderableChildren;
      }

      const nestedItems = item.children.flatMap((child: any) =>
        findSubmenuItems(child, detailsNodeName)
      );
      if (nestedItems.length > 0) {
        return nestedItems;
      }
    }
    return [];
  };

  const handleMainMenuClick = (menuTitle: string, source: "hamburger" | "main") => {
    // Track main menu item click analytics
    handleHeaderMainMenuClickDL(menuTitle, source);

    let matchedItem;
    let submenuItems: any[] = [];

    if (source === "hamburger") {
      matchedItem = hamburgerMenuList.find((item: any) => item?.hamburgerMenuTitle === menuTitle);
      submenuItems = findSubmenuItems(matchedItem, "secondaryMenuDetails");
    } else {
      matchedItem = mainMenuList.find((item: any) => item?.mainMenuLabel === menuTitle);
      submenuItems = findSubmenuItems(matchedItem, "mainMenuDetails");
    }

    setSelectedSubMenu(submenuItems);
    setSelectedTitle(menuTitle);
    setSelectedMenuCtaLink(matchedItem?.mainMenuListCta || "");
    setShowSubMenu(true);
  };

  const sidebarPanel = (
    <div
      className={`fixed top-0 left-0 z-[51] flex h-screen w-[360px] transform flex-col overflow-hidden bg-white text-white shadow-lg transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
        <div className="bg-bfl-blue flex shrink-0 items-center justify-between border-b border-white/20 px-6 py-4 md:max-h-14.5 md:min-h-14.5">
          <BFLLink
            className="flex cursor-pointer items-center"
            href={isAuthSuccessOrPerpetual ? LOGGED_IN_MY_ACC_URL : NON_LOGGED_IN_MY_ACC_URL}
            onClick={() => {
              // Track sidebar profile/sign in link click analytics
              const url = isAuthSuccessOrPerpetual
                ? LOGGED_IN_MY_ACC_URL
                : NON_LOGGED_IN_MY_ACC_URL;
              const text = isAuthSuccessOrPerpetual ? "My Account" : "Sign In";
              handleHeaderSidebarProfileClickDL(url, text);
            }}
          >
            <i className="icomoon mr-[14px] text-[20px] before:content-['\e954']"></i>
            <span className="text-base leading-none font-medium font-[Rubik,Mukta,BalooThambi2,sans-serif]">
              {" "}
              <Conditional if={!isAuthSuccessOrPerpetual}>
                <>{"Sign In"}</>
              </Conditional>
              <Conditional if={isAuthSuccessOrPerpetual}>
                <FirstName />
              </Conditional>
            </span>
          </BFLLink>
          <button
            onClick={closeSidebar}
            className="cursor-pointer text-[16px] font-[500] text-white"
            aria-label="Close menu"
          >
            <i className="icomoon before:content-['\e914']"></i>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#999] [&::-webkit-scrollbar-thumb]:rounded-lg pb-[45px] md:pb-[0px]!">
        {!showSubMenu && (
          <div
            className={`px-6 py-3 text-xl leading-[19.2px] uppercase text-gray-900 max-h-[43.2px] ${
              isWindows
                ? "font-semibold font-[Rubik,Mukta,BalooThambi2,sans-serif]"
                : "font-medium font-[Rubik-Medium,Mukta-Medium,sans-serif]"
            }`}
          >
            Main Menu
          </div>
        )}
          {!showSubMenu ? (
            <>
              <div className="space-y-2 text-gray-900">
                {hamburgerMenuList.length > 0 ? (
                  hamburgerMenuList.map((item: any) => (
                    <button
                      key={item?.key || item?.nodeName || item?.hamburgerMenuTitle}
                      className="mb-0 flex w-full cursor-pointer items-center justify-between font-[Rubik,Mukta,BalooThambi2,sans-serif] px-6 py-3 text-left text-sm hover:bg-gray-200 font-normal max-h-[41px]"
                      onClick={() => {
                        // Track analytics for hamburger menu button click
                        handleHeaderSidebarMenuButtonClickDL(item?.hamburgerMenuTitle ?? "");

                        // Execute original menu handling logic
                        handleMainMenuClick(item?.hamburgerMenuTitle, "hamburger");
                      }}
                      aria-label={`Open ${item?.hamburgerMenuTitle} submenu`}
                    >
                      {item?.hamburgerMenuTitle}
                      <i
                        className="icomoon ml-1 text-xs before:content-['\e911']"
                        aria-hidden="true"
                      ></i>
                    </button>
                  ))
                ) : (
                  <div className="px-6 py-3 text-gray-400">No menu items available</div>
                )}
              </div>

              <div className="space-y-2 border-t border-b border-gray-300 py-2 text-gray-900">
                {mainMenuList.map((item: any) => (
                  <button
                    key={item?.key || item?.nodeName || item?.mainMenuLabel}
                    className="mb-0 flex w-full items-center justify-between px-6 py-3 text-left text-sm hover:bg-gray-200"
                    onClick={() => {
                      // Track analytics for menu button click
                      handleHeaderSidebarMenuButtonClickDL(item?.mainMenuLabel ?? "");

                      // Execute original menu handling logic
                      return item?.mainMenuDetails?.length > 0 ||
                        (item?.children && item?.children.length > 0)
                        ? handleMainMenuClick(item?.mainMenuLabel, "main")
                        : null;
                    }}
                    aria-label={`Open submenu for ${item?.mainMenuLabel}`}
                  >
                    {item?.mainMenuLabel}
                    <span
                      className="icomoon text-xs before:content-['\e911']"
                      aria-hidden="true"
                    ></span>
                  </button>
                ))}
              </div>
              <FollowUsList followUsList={followUsList} />
            </>
          ) : (
            <div>
              <button
                onClick={() => setShowSubMenu(false)}
                className="flex w-full cursor-pointer items-center px-6 py-3 text-left text-base font-medium text-gray-900 uppercase bg-[#FAFAFA] max-h-[44px] border-b border-[#D7D7D7] gap-3"
              >
                <i className="bf-icon-up-arrow-long text-[14px] font-bold! -rotate-90"></i>
                 <span className="flex items-center justify-center">Main Menu</span>
              </button>
              {selectedMenuCtaLink ? (
                <BFLLink
                  href={selectedMenuCtaLink}
                  openInNewTab={false}
                  className="flex items-center justify-between px-6 py-3 bg-[#FAFAFA] cursor-pointer"
                >
                  <h3 className="text-[20px] font-medium text-gray-900 leading-[17px] font-[Rubik-Medium,Mukta-Medium,sans-serif]">{selectedTitle}</h3>
                  <i className="bf-icon-right-arrow text-[14px] text-[#FB6C0E] font-bold!"></i>
                </BFLLink>
              ) : (
                <div className="flex items-center justify-between px-6 py-3 bg-[#FAFAFA] cursor-pointer">
                  <h3 className="text-[20px] font-medium text-gray-900 leading-[17px] font-[Rubik,Mukta,BalooThambi2,sans-serif]">{selectedTitle}</h3>
                  <i className="bf-icon-right-arrow text-[14px] text-[#FB6C0E] font-bold!"></i>
                </div>
              )}
              <div className="flex items-center self-stretch gap-3 px-6 py-3 bg-white text-[#1a1a1a] cursor-default list-none max-h-[41px] text-[14px] font-medium font-[Rubik,Mukta,BalooThambi2,sans-serif]">
                {selectedTitle}
              </div>
              <div className="space-y-2 text-gray-900">
                {selectedSubMenu.map((item: any) => (
                  <React.Fragment
                    key={
                      item?.key ||
                      item?.nodeName ||
                      item?.secondaryMenuTitle ||
                      item?.mainMenuListTitle
                    }
                  >
                    <Conditional if={item.key !== "logout-settings"}>
                      <div className="mb-0 flex cursor-pointer items-center space-x-3 px-6 py-3 text-sm hover:bg-gray-200 max-h-[41px] font-normal">
                        <a
                          href={getMenuHref(item)}
                          target={shouldOpenInNewTab(item) ? "_blank" : "_self"}
                          rel="noopener noreferrer"
                          className="text-gray-900"
                        >
                          {getMenuTitle(item)}
                        </a>
                      </div>
                    </Conditional>

                    <Conditional if={item.key == "logout-settings"}>
                      <SignOutLink signoutLabel={item.mainMenuListTitle} />
                    </Conditional>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
    </div>
  );

  return (
    <>
      <button
        onClick={toggleSidebar}
        className="flex cursor-pointer border-none bg-transparent px-[6px] py-[10px] text-sm leading-[20px] hover:bg-blue-900"
        aria-label="Open menu"
      >
        <i className="icomoon mr-1 text-[18px] before:content-['\e958']" /> Menu
      </button>

      {mounted && createPortal(sidebarPanel, document.body)}

      {isSidebarOpen &&
        mounted &&
        createPortal(
          <button
            type="button"
            onClick={closeSidebar}
            data-testid="menu-overlay"
            className="fixed top-0 right-0 bottom-0 left-0 z-[50] hidden cursor-pointer border-none bg-[#000000b3] md:block"
            aria-label="Close menu overlay"
          />,
          document.body
        )}
    </>
  );
};

export default MenuToggle;
